import React from 'react';
import PropTypes from 'prop-types';
import { Divider } from 'antd';
import { EyeOutlined } from '@ant-design/icons';
import { formatTrackingTimeIST } from 'utils/tripNodeStatus';

const FLEX_BETWEEN = 'space-between';

const COLOR_TRAVELLED = '#00C853';
const COLOR_VEHICLE = '#03A9F4';
const COLOR_TEXT = '#212121';
const COLOR_MUTED = '#757575';

/**
 * Hover card on the vehicle marker: where the truck is, when it was last heard from, and
 * the state of the node it is heading for.
 */
function VehicleTooltip({ vehicleNumber, node, address, time, podRequired, onOpenImage }) {
	const row = (label, value) => (
		<div style={{ display: 'flex', justifyContent: FLEX_BETWEEN, alignItems: 'center' }}>
			<span style={{ color: COLOR_MUTED }}>{label}</span>
			{value}
		</div>
	);

	const strong = (value, extra = {}) => (
		<span style={{ fontWeight: 600, color: COLOR_TEXT, ...extra }}>{value}</span>
	);

	const imageLink = (url, type) => (
		// eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions
		<span
			style={{
				color: COLOR_TRAVELLED,
				fontWeight: 600,
				display: 'flex',
				alignItems: 'center',
				gap: 6,
				cursor: 'pointer',
			}}
			onClick={() => onOpenImage(url, type)}
		>
			Complete <EyeOutlined style={{ color: COLOR_VEHICLE, fontSize: 16 }} />
		</span>
	);

	return (
		<div
			style={{
				background: 'white',
				borderRadius: 16,
				padding: '20px',
				width: 280,
				boxShadow: '0 8px 24px rgba(0,0,0,0.12)',
				display: 'flex',
				flexDirection: 'column',
			}}
		>
			<div style={{ display: 'flex', alignItems: 'center', marginBottom: 16 }}>
				<div
					style={{
						background: COLOR_VEHICLE,
						borderRadius: 10,
						padding: '8px',
						marginRight: 12,
						display: 'flex',
						alignItems: 'center',
						justifyContent: 'center',
					}}
				>
					<img
						src="/icons/blue-truck.png"
						width="20"
						height="20"
						alt="truck"
						style={{ filter: 'brightness(0) invert(1)' }}
					/>
				</div>
				<div>
					<div style={{ fontWeight: 700, fontSize: 15, color: '#333' }}>
						{vehicleNumber || '-'}
					</div>
					<div
						style={{
							fontSize: 11,
							color: COLOR_MUTED,
							fontWeight: 600,
							marginTop: 2,
						}}
					>
						CURRENT LOCATION
					</div>
				</div>
			</div>

			<Divider style={{ margin: '0 0 16px 0' }} />

			<div style={{ fontSize: 13, display: 'flex', flexDirection: 'column', gap: 10 }}>
				{row('Heading to', strong(node?.label || '-'))}
				{row('Location', strong(address, { textAlign: 'right', maxWidth: 180 }))}
				{row('Last Updated', strong(formatTrackingTimeIST(time)))}
				{row(
					'Node Status',
					<span
						style={{
							background: '#F5F5F5',
							padding: '4px 10px',
							borderRadius: 6,
							fontSize: 11,
							fontWeight: 700,
							color: COLOR_TEXT,
						}}
					>
						{node?.status || '-'}
					</span>
				)}
				{podRequired &&
					row(
						'POD',
						node?.pod_image_url
							? imageLink(node.pod_image_url, 'pod')
							: strong('Pending')
					)}
				{node?.odometer_image_url &&
					row('Odometer', imageLink(node.odometer_image_url, 'odometer'))}
			</div>
		</div>
	);
}

VehicleTooltip.propTypes = {
	vehicleNumber: PropTypes.string,
	node: PropTypes.object,
	address: PropTypes.string,
	time: PropTypes.string,
	podRequired: PropTypes.bool,
	onOpenImage: PropTypes.func.isRequired,
};

export default VehicleTooltip;
